import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Calendar, Clock, Users, Shield } from "lucide-react";
import { useEffect, useState } from "react";

const Index = () => {
  const navigate = useNavigate();
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  
  const features = [
    {
      title: "Simple Calendar Management",
      description: "Easy-to-use interface for managing all your events",
      icon: Calendar,
    },
    {
      title: "Schedule With Friends",
      description: "Plan events together with friends and family",
      icon: Users,
    },
    {
      title: "Real-Time Updates",
      description: "Get notified instantly about schedule changes",
      icon: Clock,
    },
    {
      title: "Privacy Controls",
      description: "Control who can see your events and calendars",
      icon: Shield,
    },
  ];
  
  // All 6 images in the correct order - dashboard is first
  const images = [
    "/images/dashboard.png", // Dashboard
    "/images/calendar.png", // Calendar Demo
    "/images/friends.png", // Friends
    "/images/Bookclub.png", // Event Dialog
    "/images/family.png", // Family
    "/images/friends&family.png"  // Friends & Family
  ];
  
  // Image descriptions for better accessibility
  const imageDescriptions = [
    "Dashboard view showing event statistics and upcoming events",
    "Calendar demo with month view displaying scheduled events",
    "Friends group page showing member list and scheduled events",
    "Event dialog showing book club meeting details",
    "Family group page showing members and scheduled events",
    "Friends & Family page showing contacts and invite options"
  ];
  
  useEffect(() => {
    console.log("Current location:", window.location.href);
    console.log("Loading images from /images directory");
    
    const errorStatus: Record<string, boolean> = {};
    let loadedCount = 0;
    const totalImages = images.length;
    
    images.forEach(img => {
      const testImg = new Image();
      
      testImg.onload = () => {
        console.log(`✅ Image loaded successfully: ${img}`);
        loadedCount++;
        if (loadedCount === totalImages) {
          setImagesLoaded(true);
        }
      };
      
      testImg.onerror = () => {
        console.error(`❌ Failed to load image: ${img}`);
        errorStatus[img] = true;
        setImageErrors({...errorStatus});
      };
      
      testImg.src = img;
    });
  }, []);
  
  return (
    <div className="container mx-auto px-4 py-12">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4">Welcome to fyndtyme</h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          The easiest way to manage your schedule and coordinate events with friends and family
        </p>
        <div className="flex justify-center gap-4 mt-8">
          <Button 
            size="lg" 
            onClick={() => navigate("/signup")}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Get Started
          </Button>
          <Button 
            variant="outline" 
            size="lg" 
            onClick={() => navigate("/calendar-demo")}
            className="border-blue-600 text-blue-600 hover:bg-blue-50"
          >
            Try Demo
          </Button>
        </div>
      </div>
      
      <div className="mb-16 max-w-5xl mx-auto">
        <Carousel className="w-full" opts={{ loop: true }}>
          <CarouselContent>
            {images.map((image, index) => (
              <CarouselItem key={index}>
                <div className="p-1">
                  <Card>
                    <CardContent className="flex aspect-video items-center justify-center p-0">
                      <img 
                        src={image} 
                        alt={imageDescriptions[index]}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    </CardContent>
                  </Card>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="hidden md:block">
            <CarouselPrevious className="left-0 lg:-left-12" />
            <CarouselNext className="right-0 lg:-right-12" />
          </div>
        </Carousel>
      </div>
      
      {!imagesLoaded && Object.values(imageErrors).some(val => val) && (
        <div className="bg-yellow-100 p-4 text-yellow-700 rounded mb-8">
          <p>Some images failed to load. Check console for details.</p>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
        {features.map((feature, index) => (
          <Card key={index} className="text-center">
            <CardContent className="pt-6">
              <div className="mx-auto bg-blue-100 w-12 h-12 flex items-center justify-center rounded-full mb-4">
                <feature.icon className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
              <p className="text-gray-600">{feature.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      
      <div className="text-center">
        <h2 className="text-3xl font-bold mb-8">Ready to get started?</h2>
        <Button 
          size="lg" 
          onClick={() => navigate("/signup")}
          className="px-8 bg-blue-600 hover:bg-blue-700 text-white"
        >
          Sign Up Now
        </Button>
      </div>
    </div>
  );
};

export default Index;
